import {
  copyBytes,
  PREVIEW_TABLE_MAX_COLS,
  PREVIEW_TABLE_MAX_ROWS,
  type PreviewTable,
} from '@/utils/previewText';

const MAX_ENTRY_BYTES = 20 * 1024 * 1024;

export interface SheetPreview extends PreviewTable {
  name: string;
  sheetCount: number;
}

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localOffset: number;
}

function readU16(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! | (bytes[offset + 1]! << 8);
}

function readU32(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset]! |
      (bytes[offset + 1]! << 8) |
      (bytes[offset + 2]! << 16) |
      (bytes[offset + 3]! << 24)) >>>
    0
  );
}

function findEndOfCentralDirectory(bytes: Uint8Array): number {
  const start = Math.max(0, bytes.length - 22 - 0xffff);
  for (let offset = bytes.length - 22; offset >= start; offset -= 1) {
    if (readU32(bytes, offset) === 0x06054b50) return offset;
  }
  throw new Error('preview_sheet');
}

function listZipEntries(bytes: Uint8Array): Map<string, ZipEntry> {
  const eocd = findEndOfCentralDirectory(bytes);
  const count = readU16(bytes, eocd + 10);
  let offset = readU32(bytes, eocd + 16);
  const entries = new Map<string, ZipEntry>();
  for (let index = 0; index < count; index += 1) {
    if (readU32(bytes, offset) !== 0x02014b50) throw new Error('preview_sheet');
    const method = readU16(bytes, offset + 10);
    const compressedSize = readU32(bytes, offset + 20);
    const uncompressedSize = readU32(bytes, offset + 24);
    const nameLength = readU16(bytes, offset + 28);
    const extraLength = readU16(bytes, offset + 30);
    const commentLength = readU16(bytes, offset + 32);
    const localOffset = readU32(bytes, offset + 42);
    const name = new TextDecoder('utf-8').decode(
      bytes.subarray(offset + 46, offset + 46 + nameLength),
    );
    entries.set(name.replace(/\\/g, '/'), {
      name,
      method,
      compressedSize,
      uncompressedSize,
      localOffset,
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream !== 'function') throw new Error('preview_sheet');
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  const stream = new DecompressionStream('deflate-raw');
  // Pull inflated bytes while writing. write() never resolves if it runs
  // first and the worksheet expands past the stream buffer (a small .xlsx
  // often inflates past that). The preview then spins with no table.
  const output = new Response(stream.readable).arrayBuffer();
  const writer = stream.writable.getWriter();
  await writer.write(copy);
  await writer.close();
  const inflated = new Uint8Array(await output);
  if (inflated.byteLength > MAX_ENTRY_BYTES) throw new Error('preview_sheet');
  return inflated;
}

async function readZipEntry(bytes: Uint8Array, entry: ZipEntry): Promise<Uint8Array> {
  if (entry.uncompressedSize > MAX_ENTRY_BYTES || entry.compressedSize > MAX_ENTRY_BYTES) {
    throw new Error('preview_sheet');
  }
  if (readU32(bytes, entry.localOffset) !== 0x04034b50) throw new Error('preview_sheet');
  const nameLength = readU16(bytes, entry.localOffset + 26);
  const extraLength = readU16(bytes, entry.localOffset + 28);
  const dataStart = entry.localOffset + 30 + nameLength + extraLength;
  const compressed = bytes.subarray(dataStart, dataStart + entry.compressedSize);
  if (entry.method === 0) return compressed;
  if (entry.method === 8) return inflateRaw(compressed);
  throw new Error('preview_sheet');
}

function parseXml(xml: string): Document {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.documentElement?.localName === 'parsererror') throw new Error('preview_sheet');
  return doc;
}

function elementsByLocalName(root: ParentNode, name: string): Element[] {
  return Array.from(root.querySelectorAll('*')).filter((element) => element.localName === name);
}

function xmlText(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}

function columnIndex(letters: string): number {
  let value = 0;
  for (const char of letters) value = value * 26 + (char.charCodeAt(0) - 64);
  return value - 1;
}

function sharedString(element: Element): string {
  const parts: string[] = [];
  for (const child of Array.from(element.children)) {
    if (child.localName === 't') parts.push(child.textContent ?? '');
    if (child.localName === 'r') {
      for (const node of Array.from(child.children)) {
        if (node.localName === 't') parts.push(node.textContent ?? '');
      }
    }
  }
  return parts.join('');
}

const BUILTIN_DATE_IDS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47]);
const BUILTIN_DATE_CODES: Record<number, string> = {
  14: 'yyyy',
  15: 'yyyy',
  16: 'd',
  17: 'y',
  18: 'h',
  19: 'h',
  20: 'h',
  21: 'h',
  22: 'yyyy h',
  45: 'h',
  46: 'h',
  47: 'h',
};

function isDateFormat(formatId: number, code: string): boolean {
  if (BUILTIN_DATE_IDS.has(formatId)) return true;
  const stripped = code.replace(/\[[^\]]*]/g, '').toLowerCase();
  return /[yd]/.test(stripped) || /h/.test(stripped);
}

function dateStyleIndexes(stylesXml: string | null): Map<number, string> {
  const indexes = new Map<number, string>();
  if (!stylesXml) return indexes;
  const doc = parseXml(stylesXml);
  const custom = new Map<number, string>();
  for (const format of elementsByLocalName(doc, 'numFmt')) {
    const id = Number(format.getAttribute('numFmtId'));
    if (Number.isFinite(id)) custom.set(id, format.getAttribute('formatCode') ?? '');
  }
  const cellXfs = elementsByLocalName(doc, 'cellXfs')[0];
  const formats = cellXfs
    ? Array.from(cellXfs.children).filter((element) => element.localName === 'xf')
    : [];
  formats.forEach((format, index) => {
    const formatId = Number(format.getAttribute('numFmtId') ?? '0');
    const code = custom.get(formatId) ?? BUILTIN_DATE_CODES[formatId] ?? '';
    if (isDateFormat(formatId, code)) indexes.set(index, code);
  });
  return indexes;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function formatExcelDate(serial: number, code: string): string {
  const whole = Math.floor(serial);
  const fraction = serial - whole;
  const utc = new Date(Date.UTC(1899, 11, 30) + whole * 86_400_000);
  const date = `${pad(utc.getUTCDate())}.${pad(utc.getUTCMonth() + 1)}.${utc.getUTCFullYear()}`;
  const minutes = Math.round(fraction * 24 * 60);
  const time = `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;
  const lower = code.toLowerCase();
  const hasDate = /[yd]/.test(lower) || code === '';
  const hasTime = /h/.test(lower);
  if (!hasDate && hasTime) return time;
  if (hasTime && fraction > 1e-8) return `${date} ${time}`;
  return date;
}

function formatNumber(raw: string): string {
  if (!/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(raw)) return raw;
  const value = Number(raw);
  if (!Number.isFinite(value) || Math.abs(value) >= 1e15) return raw;
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 1e10) / 1e10);
}

function cellText(cell: Element, strings: string[], dateStyles: Map<number, string>): string {
  const type = cell.getAttribute('t') ?? '';
  const valueNode = Array.from(cell.children).find((element) => element.localName === 'v');
  const raw = valueNode?.textContent ?? '';
  if (type === 's') return strings[Number(raw)] ?? '';
  if (type === 'inlineStr') {
    const inline = Array.from(cell.children).find((element) => element.localName === 'is');
    return inline ? sharedString(inline) : '';
  }
  if (type === 'b') return raw === '1' ? 'Ja' : 'Nej';
  if (type === 'str') return raw;
  if (type === 'e') return '';
  const style = Number(cell.getAttribute('s') ?? '');
  const dateCode = dateStyles.get(style);
  if (dateCode !== undefined && /^-?\d+(\.\d+)?$/.test(raw))
    return formatExcelDate(Number(raw), dateCode);
  return formatNumber(raw);
}

function sheetTable(xml: string, strings: string[], dateStyles: Map<number, string>): PreviewTable {
  const doc = parseXml(xml);
  const cells = new Map<string, string>();
  let maxRow = 0;
  let maxCol = 0;
  let truncated = false;
  for (const cell of elementsByLocalName(doc, 'c')) {
    const ref = cell.getAttribute('r') ?? '';
    const match = /^([A-Z]+)(\d+)$/.exec(ref);
    if (!match) continue;
    const column = columnIndex(match[1] ?? '');
    const row = Number(match[2]);
    if (row < 1 || column < 0) continue;
    if (row > PREVIEW_TABLE_MAX_ROWS || column >= PREVIEW_TABLE_MAX_COLS) {
      truncated = true;
      continue;
    }
    maxRow = Math.max(maxRow, row);
    maxCol = Math.max(maxCol, column);
    cells.set(`${row}:${column}`, cellText(cell, strings, dateStyles));
  }

  const rows: string[][] = [];
  const excelRows: number[] = [];
  for (let row = 1; row <= maxRow; row += 1) {
    const line: string[] = [];
    for (let column = 0; column <= maxCol; column += 1)
      line.push(cells.get(`${row}:${column}`) ?? '');
    rows.push(line);
    excelRows.push(row);
  }
  while (rows.length > 0 && rows[0]?.every((value) => value.trim() === '')) {
    rows.shift();
    excelRows.shift();
  }
  while (rows.length > 0 && rows[rows.length - 1]?.every((value) => value.trim() === '')) {
    rows.pop();
    excelRows.pop();
  }
  return { rows, truncated, excelRows };
}

function relationshipId(sheet: Element): string {
  const direct =
    sheet.getAttribute('r:id') ||
    sheet.getAttributeNS(
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
      'id',
    );
  if (direct) return direct;
  for (const attr of Array.from(sheet.attributes)) {
    if (attr.localName === 'id') return attr.value;
  }
  return '';
}

function resolveSheetPath(target: string): string {
  const cleaned = target.replace(/\\/g, '/').replace(/^\//, '');
  const parts = (cleaned.startsWith('xl/') ? cleaned : `xl/${cleaned}`).split('/');
  const stack: string[] = [];
  for (const part of parts) {
    if (part === '..') stack.pop();
    else if (part && part !== '.') stack.push(part);
  }
  return stack.join('/');
}

export async function readFirstSheet(bytes: ArrayBuffer | ArrayBufferView): Promise<SheetPreview> {
  const view = copyBytes(bytes);
  const entries = listZipEntries(view);
  const read = async (name: string) => {
    const entry = entries.get(name);
    if (!entry) return null;
    return readZipEntry(view, entry);
  };

  const workbookBytes = await read('xl/workbook.xml');
  if (!workbookBytes) throw new Error('preview_sheet');
  const workbook = parseXml(xmlText(workbookBytes));
  const relsBytes = await read('xl/_rels/workbook.xml.rels');
  const rels = new Map<string, string>();
  if (relsBytes) {
    for (const rel of elementsByLocalName(parseXml(xmlText(relsBytes)), 'Relationship')) {
      const id = rel.getAttribute('Id');
      const target = rel.getAttribute('Target');
      if (id && target) rels.set(id, target);
    }
  }
  const sheets = elementsByLocalName(workbook, 'sheet')
    .map((sheet) => {
      const relId = relationshipId(sheet);
      return {
        name: sheet.getAttribute('name') ?? 'Ark',
        target: rels.get(relId) ?? '',
      };
    })
    .filter((sheet) => sheet.target);
  if (sheets.length === 0) {
    sheets.push({ name: 'Ark', target: 'worksheets/sheet1.xml' });
  }

  const stringsBytes = await read('xl/sharedStrings.xml');
  const strings = stringsBytes
    ? elementsByLocalName(parseXml(xmlText(stringsBytes)), 'si').map((item) => sharedString(item))
    : [];
  const stylesBytes = await read('xl/styles.xml');
  const dateStyles = dateStyleIndexes(stylesBytes ? xmlText(stylesBytes) : null);

  for (const sheet of sheets) {
    const sheetBytes = await read(resolveSheetPath(sheet.target));
    if (!sheetBytes) continue;
    const table = sheetTable(xmlText(sheetBytes), strings, dateStyles);
    return { name: sheet.name, sheetCount: sheets.length, ...table };
  }
  throw new Error('preview_sheet');
}
